import type { DeadAirFork } from './deadAirTypes'

/**
 * autoHeal — the pure decision logic for the recovery ladder. Kept free of any
 * LiveKit or DOM handle so it is unit-testable; the controller owns the actual
 * fix application and the settle/verify timing.
 *
 * The ladder has three rungs. The cheap per-peer wiring fix is the only
 * automatic one. Above it sit the two the user drives from the dialog: a room
 * reconnect, then a page reload. {@link AutoHealState} tracks how far up a
 * client has climbed.
 */

/** The fault-matched silent fix for a wiring failure. */
export type AutoHealFix = 'play' | 'resink'

/**
 * The cheap fix for a wiring failure, or null when there is none. Flow failures
 * (delivery_stalled, silent_decode) and an outright missing subscription have
 * no cheap per-peer step and go straight to the dialog.
 *
 * `not_attached` is in that same no-cheap-fix group, which is less obvious than
 * the others. Both fixes here work by acting on the track's attached elements,
 * and `not_attached` is *defined* as having none (the detector returns it when
 * `attachedElements.length === 0`), so any element-based repair for it is a
 * no-op by construction. Attaching a fresh element instead is not ours to do:
 * the app's `RoomAudioRenderer` owns attachment, and an element we created is
 * one it will never detach, so it would keep playing alongside the renderer's
 * own once React remounts. The rung would cost the peer its one cheap attempt
 * and two settle windows to accomplish nothing, so `not_attached` goes to the
 * dialog, where the reconnect rebuilds the subscription and the renderer
 * re-attaches.
 */
export function matchWiringFix(fork: DeadAirFork): AutoHealFix | null {
  switch (fork) {
    case 'lost_play':
      return 'play'
    case 'sink_divergence':
      return 'resink'
    default:
      return null
  }
}

/**
 * How far up the ladder this client has climbed since the last genuine
 * recovery. Everything here re-arms on the same edge: the detector's dead ->
 * clean transition calls {@link reset}.
 *
 * Each rung is spent at most once per cycle. That is a deliberate reading of
 * how these faults behave — a peer does not break, heal and break again; the
 * fault is continuous until something repairs it. So a repair that appears to
 * have worked but has not is the case to design for, and re-arming on apparent
 * success is what produced the old loop: the fix landed, the wiring-only probe
 * said "healed", the peer was re-armed, and the next backstop sweep 30s later
 * attempted exactly the same fix, forever, without ever escalating.
 */
export class AutoHealState {
  private readonly inFlight = new Set<string>()
  /** Peers whose one cheap fix has been used up this cycle. */
  private readonly spent = new Set<string>()
  /** Whether the user has already been through a reconnect this cycle. */
  private reconnectSpent = false
  /** Bumped by {@link reset}; identifies which cycle a verify pass belongs to. */
  private cycle = 0

  /** True only when this peer is neither mid-attempt nor already spent. */
  shouldAttempt(identity: string): boolean {
    return !this.inFlight.has(identity) && !this.spent.has(identity)
  }

  isInFlight(identity: string): boolean {
    return this.inFlight.has(identity)
  }

  markInFlight(identity: string): void {
    this.inFlight.add(identity)
  }

  /**
   * The token for the current cycle. A caller that defers work — the settle
   * window before a verify — takes one when it schedules and hands it back when
   * it runs, so state written by a pass belonging to a cycle that has since
   * ended can be dropped rather than applied to the new one.
   */
  get cycleToken(): number {
    return this.cycle
  }

  isCurrentCycle(token: number): boolean {
    return token === this.cycle
  }

  /**
   * The attempt has been verified, healed or not. Either way this peer's cheap
   * fix is used up: a "healed" verdict here is not trustworthy enough to re-arm
   * on, and a failed one has nothing left to try.
   *
   * A stale token no-ops. The settle window is long enough for a genuine
   * recovery to land inside it, and that recovery has already re-armed every
   * peer; spending them again here would send the next real break straight to
   * the dialog with the cheap fix never tried.
   */
  markVerified(identity: string, token: number): void {
    if (!this.isCurrentCycle(token)) return
    this.inFlight.delete(identity)
    this.spent.add(identity)
  }

  /**
   * The verify could not judge this attempt, so the peer goes back to where it
   * started: no longer mid-attempt, but not spent either.
   *
   * This is the attach-grace case. The probe reports `pending` for a peer whose
   * track was re-subscribed inside the settle window, because an element that
   * has not rendered yet is no evidence either way — and spending the peer on
   * that sent the next real break straight to the dialog with the cheap fix
   * never tried, which is the regression the token guard on
   * {@link markVerified} exists to prevent, reached through a different door.
   *
   * Clearing `inFlight` is not optional. A peer left mid-attempt is neither
   * attemptable nor escalatable for the rest of the cycle, so a verify that
   * simply declined to act would strand it.
   *
   * Nothing is rescheduled here. The peer is left to the next structural scan,
   * which is the thing that decides whether it is dead at all: an element that
   * did attach produces no episode, and one that did not is condemned
   * `not_attached` and escalated. That is at worst one backstop sweep away,
   * and the dialog needs two persisting episodes regardless.
   *
   * A stale token no-ops for the same reason it does in {@link markVerified}:
   * the cycle it belonged to is over and every peer has already been re-armed.
   */
  releaseInFlight(identity: string, token: number): void {
    if (!this.isCurrentCycle(token)) return
    this.inFlight.delete(identity)
  }

  /** False once the reconnect rung is used up, which promotes the dialog to reload. */
  shouldOfferReconnect(): boolean {
    return !this.reconnectSpent
  }

  markReconnectSpent(): void {
    this.reconnectSpent = true
  }

  /**
   * The room has just been rebuilt. Every subscription and attachment this
   * client had is new, so the per-peer verdicts describe wiring that no longer
   * exists: a peer whose cheap fix was spent before the reconnect has to be
   * attemptable again, or the next break for them skips straight to the dialog
   * — and, since the reconnect rung is spent by then, straight to the reload —
   * for a fault a play/resink would have healed silently.
   *
   * The reconnect rung itself deliberately survives: the user has used it.
   *
   * That survival is against a *reconnect*, not against a *recovery*. A
   * recovery edge calls {@link reset}, which gives the rung back, and it can
   * land while a reconnect is in flight — the controller spends the rung before
   * it does anything else, so a recovery arriving any time before
   * `reconnectRoom` settles un-spends it, and this method does not put it back.
   *
   * That window is not a sliver. It opens with the `startAudio` unlock, which
   * plays every attached element in the room while the room is still connected
   * and rostered, so the walk can go clean and the edge can fire — and for a
   * peer condemned over a paused element, the unlock *is* the repair. It closes
   * once `reconnectRoom` tears the room down, past which the scan is gated out
   * anyway.
   *
   * Still the intended answer, and still not re-asserted afterwards. A
   * re-assertion cannot deliver the property it would be protecting: a recovery
   * arriving a tick after the reconnect returns resets the ladder through the
   * front door regardless, so the rung's survival would come down to arrival
   * time. And it is wrong on the merits — the dead -> clean edge is the signal
   * every other rung re-arms on, so distrusting it here means distrusting
   * {@link reset} generally. A client whose audio genuinely came back should be
   * offered the reconnect again on a genuinely new break.
   *
   * Bumping the cycle also voids any verify pass still holding a pre-reconnect
   * token, which is what we want; those passes were measuring a room that is
   * gone.
   */
  onReconnect(): void {
    this.inFlight.clear()
    this.spent.clear()
    this.cycle++
  }

  /**
   * Recovery edge: forget everything so the next break starts at the bottom.
   *
   * Everything includes the reconnect rung, whether or not a reconnect is in
   * flight when this fires — see {@link onReconnect} for why that ordering is
   * deliberate.
   */
  reset(): void {
    this.onReconnect()
    this.reconnectSpent = false
  }
}
